import { ReactNode, forwardRef } from 'react';
import * as Select from '@radix-ui/react-select';
import { motion, AnimatePresence } from 'framer-motion';

export interface DropdownItem<T extends string = string> {
  value: T;
  label: string;
  /** Optional second line of context (e.g. "GPT-4o · OpenAI"). */
  hint?: string;
  /** Optional leading icon — a flag emoji, brand mark, or 12-16px svg. */
  leading?: ReactNode;
  /** Disabled items are still visible but can't be selected. */
  disabled?: boolean;
  /** When set, items with the same `group` are clustered under a heading. */
  group?: string;
}

export interface DropdownProps<T extends string = string> {
  value: T;
  onChange: (next: T) => void;
  items: DropdownItem<T>[];
  placeholder?: string;
  /** Label appearing in a tooltip / aria-label. */
  ariaLabel?: string;
  /** Visual size — `sm` matches the topbar; `md` matches Settings forms. */
  size?: 'sm' | 'md';
  /** Tightens horizontal padding when used in narrow contexts. */
  compact?: boolean;
  /** Optional className on the trigger button. */
  className?: string;
  /** Disabled state. */
  disabled?: boolean;
  /** Optional render override for the trigger's display value. */
  renderValue?: (item: DropdownItem<T> | undefined) => ReactNode;
  /** Pin the menu width to the trigger's width (default true). */
  matchTriggerWidth?: boolean;
  /** Width in px — overrides default min-width sizing. */
  width?: number | string;
}

/**
 * Canonical dropdown for Knowlez.
 *
 * Built on Radix `@radix-ui/react-select` so we get keyboard nav,
 * type-ahead, focus management, screen-reader semantics, and
 * scroll-when-tall behavior for free. Styled with the Knowlez design
 * tokens: paper card, indigo focus, hairline borders, AAA-contrast
 * dark variant.
 *
 * Two modes:
 *   - **Plain** — items[].label is the only string shown.
 *   - **Rich**  — items[].leading (flag/icon) + items[].hint (subtle
 *     subtitle line under the label) for browser/model/locale pickers.
 *
 * Use this instead of `<select>` for every product surface. Native
 * selects are kept only inside Radix-managed Modal/Form primitives
 * that already trap focus.
 */
function DropdownInner<T extends string = string>(
  props: DropdownProps<T>,
  ref?: React.Ref<HTMLButtonElement>,
): JSX.Element {
  const {
    value,
    onChange,
    items,
    placeholder,
    ariaLabel,
    size = 'sm',
    compact = false,
    className = '',
    disabled = false,
    renderValue,
    matchTriggerWidth = true,
    width,
  } = props;

  const current = items.find((it) => it.value === value);
  const triggerPadding =
    size === 'md'
      ? compact ? 'px-3 py-2' : 'px-3.5 py-2'
      : compact ? 'px-2.5 py-1.5' : 'px-3 py-1.5';
  const textSize = size === 'md' ? 'text-sm' : 'text-[13.5px]';

  // Group items if any item carries a `group` field.
  const groups = groupBy(items, (it) => it.group);

  return (
    <Select.Root value={value} onValueChange={(v) => onChange(v as T)} disabled={disabled}>
      <Select.Trigger
        ref={ref}
        aria-label={ariaLabel}
        style={width ? { width: typeof width === 'number' ? `${width}px` : width } : undefined}
        className={[
          'inline-flex items-center gap-2 rounded-lg border border-ink-200 bg-white',
          textSize,
          triggerPadding,
          'text-ink-900 hover:border-ink-300 focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'transition-colors',
          'dark:bg-ink-800/60 dark:border-ink-700 dark:text-ink-100 dark:hover:border-ink-600',
          'data-[state=open]:border-accent-500 data-[state=open]:ring-2 data-[state=open]:ring-accent-500/30',
          className,
        ].join(' ')}
      >
        <Select.Value placeholder={placeholder ?? '—'}>
          {renderValue ? renderValue(current) : (
            <span className="inline-flex items-center gap-2 truncate">
              {current?.leading && <span className="inline-flex items-center flex-shrink-0">{current.leading}</span>}
              <span className="truncate">{current?.label ?? placeholder ?? '—'}</span>
            </span>
          )}
        </Select.Value>
        <Select.Icon className="text-ink-400 ml-auto flex-shrink-0">
          <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path strokeLinecap="round" d="M3 4.5l3 3 3-3" />
          </svg>
        </Select.Icon>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          position="popper"
          sideOffset={6}
          align="start"
          className={[
            matchTriggerWidth ? 'min-w-[var(--radix-select-trigger-width)]' : '',
            'overflow-hidden rounded-xl border border-ink-100 bg-white shadow-card z-50',
            'dark:bg-ink-800 dark:border-ink-700',
          ].join(' ')}
        >
          <Select.ScrollUpButton className="flex items-center justify-center py-1 text-ink-500">
            <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path strokeLinecap="round" d="M3 7.5l3-3 3 3" />
            </svg>
          </Select.ScrollUpButton>

          <Select.Viewport className="p-1 max-h-[60vh]">
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.12, ease: [0.16, 0.84, 0.44, 1] }}
              >
                {groups.map(([group, groupItems], gi) => (
                  <div key={group ?? `__nogrp_${gi}`}>
                    {group && (
                      <Select.Group>
                        <Select.Label className="px-3 pt-2 pb-1 text-[10.5px] uppercase tracking-[0.14em] text-ink-400 font-semibold dark:text-ink-500">
                          {group}
                        </Select.Label>
                      </Select.Group>
                    )}
                    {groupItems.map((it) => (
                      <DropdownRow key={it.value} item={it} />
                    ))}
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </Select.Viewport>

          <Select.ScrollDownButton className="flex items-center justify-center py-1 text-ink-500">
            <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path strokeLinecap="round" d="M3 4.5l3 3 3-3" />
            </svg>
          </Select.ScrollDownButton>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}

function DropdownRow({ item }: { item: DropdownItem }) {
  return (
    <Select.Item
      value={item.value}
      disabled={item.disabled}
      className={[
        'relative flex items-start gap-2.5 rounded-md px-2.5 py-2 cursor-pointer outline-none',
        'text-[13.5px] text-ink-700 dark:text-ink-200',
        'data-[highlighted]:bg-accent-50 data-[highlighted]:text-ink-900',
        'dark:data-[highlighted]:bg-accent-500/15 dark:data-[highlighted]:text-ink-50',
        'data-[state=checked]:font-semibold data-[state=checked]:text-accent-700 dark:data-[state=checked]:text-accent-300',
        'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
      ].join(' ')}
    >
      {item.leading && (
        <span className="mt-0.5 inline-flex items-center justify-center flex-shrink-0 text-[15px] leading-none">
          {item.leading}
        </span>
      )}
      <span className="flex flex-col gap-0.5 min-w-0 flex-1">
        <Select.ItemText>{item.label}</Select.ItemText>
        {item.hint && (
          <span className="text-[11.5px] text-ink-500 dark:text-ink-400 truncate leading-snug">
            {item.hint}
          </span>
        )}
      </span>
      <Select.ItemIndicator className="ml-2 mt-0.5 flex-shrink-0 text-accent-600 dark:text-accent-400">
        <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 8.5l3 3 7-7" />
        </svg>
      </Select.ItemIndicator>
    </Select.Item>
  );
}

function groupBy<T>(items: T[], key: (it: T) => string | undefined): [string | undefined, T[]][] {
  const map = new Map<string | undefined, T[]>();
  for (const it of items) {
    const k = key(it);
    const arr = map.get(k);
    if (arr) arr.push(it);
    else map.set(k, [it]);
  }
  return Array.from(map.entries());
}

export const Dropdown = forwardRef(DropdownInner) as <T extends string = string>(
  props: DropdownProps<T> & { ref?: React.Ref<HTMLButtonElement> },
) => JSX.Element;
